import { fotoFecha, fotoURL } from "@/helpers/foto";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef, useState, type JSX, type ReactNode } from "react";
import { Big } from "./Big";
import { useConfigContext } from "@/state/configContext";
import {
  faCheck,
  faDownload,
  faShoppingCart,
} from "@fortawesome/free-solid-svg-icons";
import { Fa } from "@/helpers/Fa";
import { comprarFoto } from "@/state/server";
import { useAppContext } from "../state/appContext";
import type { Foto } from "@/state/types";
import {
  applyEdits,
  EditorOpsZod,
  loadImageToCanvas,
} from "../editor/editorCore";
import { urlToDataUrl } from "@/state/api";

export function Galeria(props: {
  fotos: Foto[];
  selected?: number;
  onSelected?: (idx: number) => unknown;
}) {
  return <GaleriaBig {...props} icons={GaleriaIcons} />;
}

export function GaleriaBig({
  fotos,
  icons,
  container: Container = ({ children }) => <>{children}</>,
  selected: defaultSelected,
  onSelected,
}: {
  fotos: Foto[];
  icons: (f: {
    fotos: Foto[];
    foto: Foto;
    setActive: () => unknown;
  }) => JSX.Element;
  container?: ({ children }: { children: ReactNode }) => JSX.Element;
  selected?: number;
  onSelected?: (idx: number) => unknown;
}) {
  const [big, setBig] = useState(false);
  const [selected, setSelected_] = useState(defaultSelected || -1); // TODO: <number | undefined>

  const setSelected = (idx: number) => {
    onSelected?.(idx);
    setSelected_(idx);
  };

  return big ? (
    <Big
      Icons={icons}
      fotos={fotos}
      selected={selected}
      onSelected={setSelected}
      onClose={() => setBig(false)}
    />
  ) : (
    <Container>
      {fotos.length === 0 ? (
        "No hay fotos"
      ) : (
        <GaleriaInner
          Icons={icons}
          fotos={fotos}
          selected={selected}
          onSelected={(idx: number, big = false) => {
            setSelected(idx);
            setBig(big);
          }}
        />
      )}
    </Container>
  );
}

const columnWidth = 200;
const rowHeight = 275;

export function GaleriaInner({
  Icons,
  fotos,
  selected,
  onSelected,
}: {
  Icons: (props: {
    fotos: Foto[];
    foto: Foto;
    setActive: () => unknown;
  }) => JSX.Element;
  fotos: Foto[];
  selected: number;
  onSelected: (idx: number, big?: boolean) => unknown;
}) {
  const parentRef = useRef<HTMLDivElement>(null);

  // Leer clientWidth durante el render daba 0 en el primer pase (el ref todavia
  // es null) y nada lo recalculaba despues, ni al rotar el telefono.
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const columnCount = Math.max(1, Math.floor(width / columnWidth));
  const marginLeft = Math.max(0, (width - columnCount * columnWidth) / 2);
  const rowCount = Math.ceil(fotos.length / columnCount);

  const scrollMargin = parentRef.current?.offsetTop ?? 0;
  const rowVirtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => rowHeight,
    overscan: 2,
    scrollMargin,
  });

  useEffect(() => {
    if (selected < 0) return; // TODO: deberia ser number | undefined ?
    if (columnCount < 2) return;
    const row = Math.floor(selected / columnCount);
    scrollIntoViewCentered(
      scrollMargin + row * rowHeight,
      scrollMargin + row * rowHeight + rowHeight
    );
  }, [selected, columnCount]);

  return (
    <div
      ref={parentRef}
      style={{
        height: rowVirtualizer.getTotalSize(),
        width: "100%",
        position: "relative",
      }}
    >
      {rowVirtualizer.getVirtualItems().map((row) =>
        Array.from({ length: columnCount }, (_, colIndex) => {
          const idx = row.index * columnCount + colIndex;
          const foto = fotos[idx];
          if (!foto) return null;

          return (
            <div
              key={`${row.index}-${colIndex}`}
              style={{
                position: "absolute",
                top: row.start - scrollMargin,
                left: marginLeft + columnWidth * colIndex,
                width: columnWidth,
                height: row.size,
              }}
            >
              <div
                className={`minifoto ${idx == selected ? "resaltar" : ""}`}
                onClick={() => onSelected(idx, true)}
              >
                {/* TODO: si está editada, modificar */}
                <img src={fotoURL(foto, "small")} />
              </div>
              <div className="minifoto_det">
                <div className="det">
                  {fotoFecha(foto)}
                  {foto.edicion && " - EDITADA"}
                </div>
                {
                  <Icons
                    fotos={fotos}
                    foto={foto}
                    setActive={() => onSelected(idx)}
                  />
                }
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

export function GaleriaIcons({
  fotos,
  foto,
  setActive,
}: {
  fotos: Foto[];
  foto: Foto;
  setActive: () => unknown;
}) {
  const config = useConfigContext();
  const status = useAppContext();
  const usuario = status.state.usuario;

  const download = (
    <Fa icon={faDownload} style={{ fontSize: "4em", color: "#5cb85c" }} />
  );

  return (
    <div className="icons">
      {config.web && usuario.compradas?.[foto.i] ? (
        foto.edicion ? (
          <span
            onClick={() => {
              setActive();
              downloadFoto(foto);
            }}
          >
            {download}
          </span>
        ) : (
          <a href={fotoURL(foto, "originales")} target="_blank">
            {download}
          </a>
        )
      ) : usuario?.encargadas[foto.i] ? (
        <Fa icon={faCheck} style={{ fontSize: "4em" }} />
      ) : (
        <a
          onClick={(e) => {
            setActive();
            comprarFoto(foto, fotos, e.metaKey, usuario.encargadas ?? {});
          }}
        >
          <Fa icon={faShoppingCart} style={{ fontSize: "4em" }} />
        </a>
      )}
    </div>
  );
}

async function downloadFoto(foto: Foto) {
  try {
    const url = await urlToDataUrl(fotoURL(foto, "originales"));
    const ops = EditorOpsZod.parse(JSON.parse(foto.edicion ?? ""));
    const canvas = document.createElement("canvas");
    const img = await loadImageToCanvas(url, canvas);
    applyEdits(canvas, img, ops);
    downloadCanvas(canvas, `${foto.i}-editada.jpg`);
  } catch (e) {
    console.error(e);
  }
}

const downloadCanvas = (canvas: HTMLCanvasElement, filename = "edited.png") => {
  const link = document.createElement("a");
  link.download = filename;
  link.href = canvas.toDataURL("image/jpeg"); // or "image/jpeg"
  link.click();
};

function scrollIntoViewCentered(y1: number, y2: number) {
  const viewportTop = window.scrollY;
  const viewportBottom = viewportTop + window.innerHeight;

  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);

  // Check if both coordinates are already in view
  if (minY >= viewportTop && maxY <= viewportBottom) {
    return; // Already visible, no need to scroll
  }

  // Compute center point between the two y-coords
  const centerY = (minY + maxY) / 2;

  // Scroll so that centerY is in the middle of the viewport
  const targetScroll = centerY - window.innerHeight / 2;

  window.scrollTo({ top: targetScroll });
}
